/**
 * The SectionTypes that have meaningful handling in the recipient flow (mirrors the logic in
 * Reapit.Services.Forms.Business/ModelBuilders/RecipientFlowBuilders/RecipientCardModelBuilder.cs and
 * RecipientStepModelBuilder.cs). Used by the card drawer to only offer real supported types as choices.
 *
 * `Signature` is included here because RecipientStepModelBuilder uses it to gate the Continue/Submit button
 * — a step containing a Signature card only allows the recipient to proceed once they have signed
 * (documentPerson.Signature != null). Without this type being selectable, admins could accidentally overwrite
 * a seeded Signature card's section type to None, removing the signature gate and allowing unsigned submission.
 *
 * Every other excluded SectionType (e.g. StandardTerm, Document, Link, RecipientSignature,
 * RepresentativeSignature) has no dedicated handling and falls through to the same generic
 * manually-authored-fields rendering as "None".
 */
export const TYPED_RECIPIENT_SECTION_TYPES = new Set([
  'PropertyAddress',
  'PersonUser',
  'PersonRecipient',
  'PersonRepresentee',
  'PaymentMethod',
  'SpecialCondition',
  'AdditionalClause',
  'RepairerElectrical',
  'RepairerPlumbing',
  'RepairerOther',
  'Signature',
])

/**
 * The subset of TYPED_RECIPIENT_SECTION_TYPES whose card content is actually built by a dedicated
 * RecipientCardModelBuilder ComponentFactory branch (PropertyAddressComponentFactory, AgencyComponentFactory,
 * etc. — see the if/else-if chain in RecipientCardModelBuilder.BuildAsync), i.e. the card's own
 * RecipientFields are never read and manual field-authoring in the builder would be pointless.
 *
 * `Signature` is deliberately EXCLUDED: RecipientCardModelBuilder has no dedicated branch for it at all —
 * it falls through to the same generic "read this card's own RecipientFields" branch as `None`. The
 * signature pad itself only appears because a RecipientField with ActionType `Signing` exists on the card
 * (see BuildFieldDataAsync's `RecipientActionType.Signing` case); SectionType.Signature's only other effect
 * is gating the step's Continue button (RecipientStepModelBuilder). So a Signature card genuinely needs
 * manually-authored fields (a Main/Signing field + an optional Additional acknowledgement field), exactly
 * like the legacy hand-seeded pattern. Gate "+ Add Field" / the read-only factory caption on this set, not
 * on TYPED_RECIPIENT_SECTION_TYPES, so Signature cards keep manual field authoring.
 */
export const AUTO_GENERATED_RECIPIENT_SECTION_TYPES = new Set(
  [...TYPED_RECIPIENT_SECTION_TYPES].filter((sectionType) => sectionType !== 'Signature'),
)
